/**
 * AgentActivityPanel — "AI Pipeline" card.
 *
 * Rebuilt to the AI_Pipeline_Redesign template: DM Serif header, a stage strip
 * with a connector line (green done → orange active), and a roster table.
 * Token-based so it renders in both Daylight and Dark. Wired to the real
 * pipeline data from useAgentUpdates — engines/data untouched, presentation only.
 */

import React, { useState } from 'react';
import type { AgentStatus, AgentStatusType } from './types';
import { useAgentUpdates } from './hooks/useAgentUpdates';
import { RefreshCw, ChevronDown, ChevronUp, Wifi, WifiOff } from 'lucide-react';

interface AgentActivityPanelProps {
  claimId?: string;
  showTimeline?: boolean;
  defaultExpanded?: boolean;
  onAgentClick?: (agentId: string, claimId?: string) => void;
}

type StageState = 'done' | 'active' | 'idle';

const GLYPHS = ['▤', '⇄', '◬', '⊞', '§', '⇌', '≣', '◧', '⊟', '⊕', '≋', '◈', '⊠', '✦'];

function stateOf(status: AgentStatusType): StageState {
  if (status === 'complete') return 'done';
  if (status === 'running' || status === 'waiting_human') return 'active';
  return 'idle';
}

export default function AgentActivityPanel({
  claimId,
  defaultExpanded = true,
  onAgentClick,
}: AgentActivityPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { pipeline, loading, error, refresh, connected } = useAgentUpdates(claimId);

  const agents: AgentStatus[] = pipeline?.agents ?? [];
  const runningCount = agents.filter((a) => a.status === 'running' || a.status === 'waiting_human').length;
  const completeCount = agents.filter((a) => a.status === 'complete').length;
  const hasActivity = runningCount > 0 || Boolean(pipeline?.pendingHumanReview);
  const donePct = agents.length ? Math.round((completeCount / agents.length) * 100) : 0;

  // ---- node circle (stage strip) ----
  const circleStyle = (s: StageState): React.CSSProperties => {
    const base: React.CSSProperties = {
      width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: '1.25rem', flex: '0 0 48px', position: 'relative', zIndex: 1,
    };
    if (s === 'done') return { ...base, background: 'transparent', border: '1px solid rgba(34,197,94,0.3)', color: 'var(--color-verified)' };
    if (s === 'active') return { ...base, background: 'var(--color-needs-human-tint)', border: '1px solid rgba(255,102,0,0.4)', color: 'var(--color-brand)', animation: 'cs-ring 1.8s ease-out infinite' };
    return { ...base, background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-dim)' };
  };
  const subStyle = (s: StageState): React.CSSProperties => ({
    fontSize: '0.6875rem', fontWeight: 500,
    color: s === 'done' ? 'var(--color-verified)' : s === 'active' ? 'var(--color-needs-human-text)' : 'var(--color-text-ghost)',
  });
  const subText = (a: AgentStatus, s: StageState) =>
    s === 'done' ? 'Complete' : s === 'active' ? (a.progress ? `Running · ${a.progress.current}` : 'Running') : 'Queued';

  // ---- roster ----
  const markStyle = (s: StageState): React.CSSProperties => {
    const base: React.CSSProperties = { width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.875rem', flex: '0 0 28px' };
    if (s === 'done') return { ...base, background: 'transparent', color: 'var(--color-verified)' };
    if (s === 'active') return { ...base, background: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' };
    return { ...base, background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)' };
  };
  const chipStyle = (s: StageState): React.CSSProperties => {
    const base: React.CSSProperties = { display: 'inline-flex', fontSize: '0.6875rem', fontWeight: 600, padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap', border: '1px solid' };
    if (s === 'done') return { ...base, background: 'transparent', color: 'var(--color-verified)', borderColor: 'rgba(34,197,94,0.25)' };
    if (s === 'active') return { ...base, background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'rgba(255,102,0,0.28)' };
    return { ...base, background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)', borderColor: 'var(--color-border)' };
  };
  const chipText = (s: StageState) => (s === 'done' ? 'Complete' : s === 'active' ? 'Running' : 'Queued');
  const casesText = (a: AgentStatus, s: StageState) => (a.progress ? `${a.progress.current}/${a.progress.total}` : s === 'done' ? '✓' : '—');

  const GRID = '1.5fr 1.7fr 0.8fr 0.9fr 0.7fr';

  return (
    <div className="rounded-[14px] overflow-hidden border" style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* header */}
      <div
        className="flex items-center gap-3 cursor-pointer"
        style={{ padding: '18px 22px', borderBottom: expanded ? '1px solid var(--color-border-sub)' : 'none' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--color-needs-human-tint)', border: '1px solid rgba(255,102,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-brand)', fontSize: '1.125rem', flex: '0 0 38px' }}>◧</div>
        <div className="flex-1 min-w-0">
          <h2 className="font-serif" style={{ fontSize: '1.3125rem', color: 'var(--color-text-primary)', margin: 0, fontWeight: 400, lineHeight: 1.1 }}>AI Pipeline</h2>
          <div style={{ fontSize: '0.7812rem', color: 'var(--color-text-muted)', marginTop: 3 }}>
            <span className="font-mono" style={{ color: 'var(--color-text-primary)' }}>{runningCount}</span> active
            {' · '}{agents.length || 14}-stage pipeline{' · '}LLMs interpret, code calculates
          </div>
        </div>
        {hasActivity && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--color-needs-human-tint)', border: '1px solid rgba(255,102,0,0.28)', borderRadius: 999, padding: '5px 12px', fontSize: '0.75rem', color: 'var(--color-needs-human-text)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-brand)', display: 'inline-block', animation: 'cs-pulse 1.4s ease-in-out infinite' }} />Processing
          </div>
        )}
        <span style={{ color: connected ? 'var(--color-verified)' : 'var(--color-text-dim)' }} title={connected ? 'Real-time updates active' : 'Using polling updates'}>
          {connected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); refresh(); }}
          disabled={loading}
          style={{ width: 34, height: 34, borderRadius: 9, border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)', fontSize: '0.9375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          aria-label="Refresh pipeline"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {expanded ? <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} /> : <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />}
      </div>

      {expanded && (
        error ? (
          <div className="text-center" style={{ padding: '32px', color: 'var(--color-text-muted)' }}>
            <p>{error}</p>
            <button className="mt-2 hover:underline text-sm" style={{ color: 'var(--color-needs-human-text)' }} onClick={refresh}>Try again</button>
          </div>
        ) : loading && !pipeline ? (
          <div className="flex items-center justify-center" style={{ padding: '40px' }}>
            <RefreshCw className="w-6 h-6 animate-spin" style={{ color: 'var(--color-brand)' }} />
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center" style={{ padding: '40px', color: 'var(--color-text-muted)' }}>
            <p className="text-sm">No active AI agents</p>
            <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Upload documents to start processing</p>
          </div>
        ) : (
          <>
            {/* stage strip */}
            <div style={{ padding: '24px 30px 26px' }}>
              <div style={{ fontSize: '0.6562rem', letterSpacing: '1.3px', color: 'var(--color-text-dim)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 20 }}>Processing timeline</div>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: 24, left: '7.1%', right: '7.1%', height: 2, background: 'var(--color-border-sub)', borderRadius: 2 }} />
                <div style={{ position: 'absolute', top: 24, left: '7.1%', width: `${Math.max(0, donePct - 7) * 0.858}%`, height: 2, background: 'var(--color-verified)', borderRadius: 2 }} />
                <div style={{ display: 'flex', position: 'relative', zIndex: 1 }}>
                  {agents.map((a, i) => {
                    const s = stateOf(a.status);
                    return (
                      <div key={a.agentId} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10, minWidth: 0 }}>
                        <div style={circleStyle(s)}>{GLYPHS[i % GLYPHS.length]}</div>
                        <div style={{ fontSize: '0.7812rem', color: 'var(--color-text-primary)', fontWeight: 500, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{a.agentName.replace(/ AI$/, '')}</div>
                        <div style={subStyle(s)}>{subText(a, s)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* roster table */}
            <div style={{ borderTop: '1px solid var(--color-border-sub)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 14, padding: '11px 22px', background: 'var(--color-bg-base)', borderBottom: '1px solid var(--color-border-sub)' }}>
                {['Agent', 'Specialty', 'Stage', 'Status', 'Cases'].map((h, i) => (
                  <div key={h} style={{ fontSize: '0.6562rem', letterSpacing: '0.6px', color: 'var(--color-text-dim)', textTransform: 'uppercase', fontWeight: 600, textAlign: i === 4 ? 'right' : 'left' }}>{h}</div>
                ))}
              </div>
              {agents.map((a, i) => {
                const s = stateOf(a.status);
                return (
                  <div
                    key={a.agentId}
                    onClick={() => onAgentClick?.(a.agentId, claimId)}
                    className="cs-roster-row"
                    style={{ display: 'grid', gridTemplateColumns: GRID, gap: 14, padding: '13px 22px', borderBottom: '1px solid var(--color-border-faint)', alignItems: 'center', cursor: onAgentClick ? 'pointer' : 'default' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-base)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                      <div style={markStyle(s)}>{GLYPHS[i % GLYPHS.length]}</div>
                      <span style={{ fontSize: '0.8438rem', color: 'var(--color-text-primary)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.agentName.replace(/ AI$/, '')}</span>
                    </div>
                    <div style={{ fontSize: '0.7812rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.persona}</div>
                    <div className="font-mono" style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Stage {i + 1}</div>
                    <div><span style={chipStyle(s)}>{chipText(s)}</span></div>
                    <div className="font-mono" style={{ fontSize: '0.8125rem', textAlign: 'right', color: s === 'active' ? 'var(--color-needs-human-text)' : a.progress ? 'var(--color-text-secondary)' : 'var(--color-text-ghost)', fontWeight: s === 'active' ? 500 : 400 }}>{casesText(a, s)}</div>
                  </div>
                );
              })}
            </div>
          </>
        )
      )}
    </div>
  );
}
